import { z } from 'zod';

/**
 * Шкала процента награды партнёру — единственный источник (#167): из неё берут
 * `@upme/domain` и `@upme/view-logic`. Меняя шкалу, поправь CHECK
 * `Event_partner_program_check` миграцией.
 */
export const PARTNER_REWARD_PERCENTS = [10, 15, 20, 25, 30, 40, 50] as const;
export type PartnerRewardPercent = (typeof PARTNER_REWARD_PERCENTS)[number];

/** Партнёрская программа события (#150, задача 5a). */
export const PartnerRewardPercentSchema = z.union(PARTNER_REWARD_PERCENTS.map((v) => z.literal(v)));

export const PartnerProgramInput = z.object({
  rewardPercent: PartnerRewardPercentSchema,
  seatsLimit: z.number().int().min(1).max(1000),
});
export type PartnerProgramInput = z.infer<typeof PartnerProgramInput>;

export const PartnerProgramDto = z.object({
  rewardPercent: z.number().int(),
  rewardEnergy: z.number().int(),
  /** round(rewardEnergy × 100 / ENERGY_PER_USD). */
  rewardUsdCents: z.number().int(),
  seatsLimit: z.number().int(),
  seatsTaken: z.number().int(),
  /** Остаток замороженного пула — только организатору, остальным null. */
  poolRemainingEnergy: z.number().int().nullable(),
});
export type PartnerProgramDto = z.infer<typeof PartnerProgramDto>;

export const PartnerProgramQuoteQuery = z.object({
  price: z.coerce.number().int().positive(),
  rewardPercent: z.coerce.number().pipe(PartnerRewardPercentSchema),
  seatsLimit: z.coerce.number().int().min(1).max(1000),
  /** Событие правится: валюта его города, а его текущий пул считается доступным. */
  eventId: z.uuid().optional(),
});
export type PartnerProgramQuoteQuery = z.infer<typeof PartnerProgramQuoteQuery>;

export const PartnerProgramQuoteDto = z.object({
  available: z.boolean(),
  /** Причина недоступности курса (FxUnavailableReason), null при available. */
  reason: z.string().nullable(),
  /** Валюта города, по которой считалась котировка (#169). */
  currency: z.string().length(3),
  rewardEnergy: z.number().int(),
  rewardUsdCents: z.number().int(),
  poolEnergy: z.number().int(),
  purchasedAvailable: z.number().int(),
  shortfall: z.number().int(),
  /** Минимальная награда партнёру в городе, центы USD; null — без минимума (#168). */
  minRewardUsdCents: z.number().int().nullable(),
  /** Проценты шкалы, при которых награда не ниже минимума; [] — курс недоступен или не дотягивает ни один. */
  allowedPercents: z.array(PartnerRewardPercentSchema),
});
export type PartnerProgramQuoteDto = z.infer<typeof PartnerProgramQuoteDto>;

export const ReferralVisitInput = z.object({
  ref: z
    .string()
    .regex(/^[a-z0-9]{5}$/i)
    .transform((s) => s.toLowerCase()),
});
export type ReferralVisitInput = z.infer<typeof ReferralVisitInput>;

/** Приглашённый партнёра на событие (#156): только имя, аватар и факты для статуса. */
export const PartnerInviteDto = z.object({
  userId: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  photoUrl: z.string().nullable(),
  /** Последняя заявка гостя на оплату. */
  requestStatus: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
  /** Билет гостя на это событие; null — билета нет. */
  ticketStatus: z.enum(['ACTIVE', 'CHECKED_IN', 'CANCELLED']).nullable(),
  /** Билет засчитан этому партнёру (Ticket.partnerUserId = viewer). */
  countedForMe: z.boolean(),
  /** createdAt последней заявки — порядок списка. */
  submittedAt: z.iso.datetime(),
});
export type PartnerInviteDto = z.infer<typeof PartnerInviteDto>;

export const PartnerInvitesResponse = z.object({ items: z.array(PartnerInviteDto) });
export type PartnerInvitesResponse = z.infer<typeof PartnerInvitesResponse>;
